import React, { useMemo } from 'react';
import { Box, CircularProgress, IconButton, Theme, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { FileCopyOutlined, OpenInNew } from '@material-ui/icons';
import Icon from '@mdi/react';
import { mdiQrcode } from '@mdi/js';
import QRCode from 'react-qr-code';
import { copyTextToClipboard } from '../../../../../../../../common/CopyToClipboard';
import { QRCodeDownloadMenu } from '../../../../../../../../common/qr-code/QRCodeDownloadMenu';
import { useSnackbar } from '../../../../../../../../global/config/useSnackbar';
import { FormQRCodePrintPage } from '../../../../../../../admin/forms/FormsList/FormQRCodePrintPage';
import { FunnelQRCodePrintPage } from '../../../../../../../admin/admin-console/admin-console-content-pages/admin-console-funnels/FunnelQRCodePrintPage';
import { KeyLink } from '../../../dashboard-widget-setting/dashboard-widget-model';
import { KeyLinkOption, keyLinkKey, useKeyLinkOptions } from './useKeyLinkOptions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    list: {
      width: '100%',
      overflowY: 'auto',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    type: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.color.grey.neutral500,
      width: '64px',
      flexShrink: 0,
    },
    label: {
      flex: 1,
      minWidth: 0,
      fontSize: '14px',
      color: theme.color.grey.neutral500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    expired: {
      fontSize: '12px',
      color: theme.color.grey.neutral300,
      marginLeft: '8px',
    },
    empty: {
      fontSize: '14px',
      color: theme.color.grey.neutral300,
      padding: '24px 0',
      textAlign: 'center',
    },
  })
);

const typeLabels = {
  form: 'Form',
  funnel: 'Funnel',
  apply: 'Apply',
};

const QrTrigger = (onClick: (e: React.MouseEvent<HTMLElement>) => void) => (
  <Tooltip title='QR code'>
    <IconButton size='small' onClick={onClick}>
      <Icon path={mdiQrcode} size={0.75} />
    </IconButton>
  </Tooltip>
);

const QrAction = ({ link, orgLogo }: { link: KeyLinkOption; orgLogo: string | null }) => {
  if (link.type === 'form' && link.id) {
    return <FormQRCodePrintPage formId={link.id} formTitle={link.label} orgLogo={orgLogo} trigger={QrTrigger} />;
  }

  if (link.type === 'funnel' && link.slug) {
    return (
      <FunnelQRCodePrintPage
        slug={link.slug}
        funnelName={link.label}
        description={link.description}
        image={link.image}
        orgLogo={orgLogo}
        trigger={QrTrigger}
      />
    );
  }

  return (
    <QRCodeDownloadMenu
      value={link.url}
      fileName={`${link.label} QR code`}
      trigger={QrTrigger}
      printContent={
        <Box
          style={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {orgLogo && <img src={orgLogo} alt='' style={{ maxHeight: '80px', marginBottom: '32px' }} />}
          <QRCode value={link.url} size={320} />
          <Typography style={{ marginTop: '24px', fontSize: '24px', fontWeight: 700 }}>{link.label}</Typography>
        </Box>
      }
    />
  );
};

export const KeyLinksTableWidget = ({ filters }: { filters: string }) => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const { options, orgLogo, loading } = useKeyLinkOptions();

  const links = useMemo(() => {
    const selected: KeyLink[] = JSON.parse(filters).keyLinks ?? [];
    const optionsByKey = new Map(options.map((option) => [keyLinkKey(option), option]));
    return selected.map((link) => optionsByKey.get(keyLinkKey(link))).filter(Boolean) as KeyLinkOption[];
  }, [filters, options]);

  const onCopy = (url: string) => {
    copyTextToClipboard(url)
      .then(() => setSnackbar({ open: true, message: 'Link copied to clipboard', variant: 'success' }))
      .catch(() => setSnackbar({ open: true, message: 'Failed to copy', variant: 'error' }));
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (links.length === 0) {
    return <Typography className={classes.empty}>No links selected. Edit this widget to add some.</Typography>;
  }

  return (
    <Box className={classes.list}>
      {links.map((link) => (
        <Box key={keyLinkKey(link)} className={classes.row}>
          <Typography className={classes.type}>{typeLabels[link.type]}</Typography>
          <Typography className={classes.label} title={link.url}>
            {link.label}
            {!link.isActive && <span className={classes.expired}>Expired</span>}
          </Typography>
          <Tooltip title='Copy link'>
            <IconButton size='small' onClick={() => onCopy(link.url)}>
              <FileCopyOutlined fontSize='small' />
            </IconButton>
          </Tooltip>
          {link.isActive && <QrAction link={link} orgLogo={orgLogo} />}
          <Tooltip title='Open'>
            <IconButton size='small' onClick={() => window.open(link.url, '_blank', 'noopener')}>
              <OpenInNew fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
};
